/**
 * `HotlineClient` — the single entry point apps construct.
 *
 * Wires together the HTTP transport, the typed REST API, and the realtime
 * Socket.IO client over one shared `TokenStore`, so a login/logout/refresh in
 * one place is visible everywhere (the realtime handshake re-reads the same
 * token). The React dashboard creates exactly one of these.
 */
import type { TokenPair } from '@hotline/contracts';

import { HotlineApi } from './api.js';
import { HttpClient, type FetchLike } from './http.js';
import { RealtimeClient, type SocketFactory } from './realtime.js';
import { createLocalStorageTokenStore } from './token-store.js';
import type { TokenStore } from './types.js';

export interface HotlineClientOptions {
  /** Backend base URL, e.g. `https://api.hotline.red` or `http://localhost:8003`. */
  baseUrl: string;
  /** Token persistence (defaults to a localStorage-backed store). */
  tokenStore?: TokenStore;
  /** Injected fetch (tests). */
  fetch?: FetchLike;
  /** Injected Socket.IO factory (tests). */
  socketFactory?: SocketFactory;
  /** Fired when the session becomes unrecoverable (refresh failed) → redirect to login. */
  onAuthError?: () => void;
}

export class HotlineClient {
  readonly http: HttpClient;
  readonly api: HotlineApi;
  readonly realtime: RealtimeClient;
  readonly tokenStore: TokenStore;

  constructor(options: HotlineClientOptions) {
    this.tokenStore = options.tokenStore ?? createLocalStorageTokenStore();
    this.http = new HttpClient({
      baseUrl: options.baseUrl,
      tokenStore: this.tokenStore,
      fetch: options.fetch,
      onAuthError: options.onAuthError,
    });
    this.api = new HotlineApi(this.http);
    this.realtime = new RealtimeClient({
      baseUrl: options.baseUrl,
      getToken: () => this.tokenStore.get()?.accessToken ?? null,
      socketFactory: options.socketFactory,
    });
  }

  /** True when an access token is currently stored. */
  get isAuthenticated(): boolean {
    return Boolean(this.tokenStore.get()?.accessToken);
  }

  /** Current persisted token pair (or null). */
  get tokens(): TokenPair | null {
    return this.tokenStore.get();
  }

  /** Clear the session and tear down the realtime socket. */
  logout(): void {
    this.api.logout();
    this.realtime.disconnect();
  }
}
